import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiCheck, FiCalendar, FiUser, FiBookOpen, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import { utcToChicago } from '../../utils/datetime-comprehensive';
import { DateTime } from 'luxon';
import apiService from '../../services/api';
import versionService from '../../services/versionService';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import { getCheckedInTimestamp, hasCheckedInRecord } from '../../utils/attendanceSessionStatus';
import styles from './ActiveAttendanceSessions.module.css';

// Helper to detect if an error is likely due to a deployment
const isDeploymentRelatedError = (error) => {
  if (!error) return false;

  // Check for common deployment-related error patterns
  const errorMessage = error.message || error.toString() || '';
  const errorStatus = error.status || error.response?.status;

  // 503 Service Unavailable - common during deployments
  if (errorStatus === 503) return true;

  // Network errors during deployment
  if (errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('NetworkError') ||
      errorMessage.includes('Network request failed')) {
    return true;
  }

  // HTML returned instead of JSON (server restarting, returning error page)
  if (errorMessage.includes('Unexpected token') ||
      errorMessage.includes('SyntaxError') ||
      errorMessage.includes('<!DOCTYPE') ||
      errorMessage.includes('<html')) {
    return true;
  }

  // Chunk loading errors
  if (errorMessage.includes('Loading chunk') ||
      errorMessage.includes('ChunkLoadError')) {
    return true;
  }

  return false;
};

function ActiveAttendanceSessions() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [checkingIn, setCheckingIn] = useState(new Set());
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const deploymentErrorCountRef = useRef(0);
  const pollingPausedRef = useRef(false);

  // Check if an update is available when deployment-related errors occur
  const checkForUpdate = useCallback(async () => {
    try {
      const hasUpdate = await versionService.checkForUpdates();
      if (hasUpdate) {
        setUpdateAvailable(true);
        pollingPausedRef.current = true; // Stop polling when update is detected
        logger.info('App update detected during attendance fetch - pausing polling');
      }
      return hasUpdate;
    } catch (e) {
      logger.warn('Failed to check for updates', { error: e });
      return false;
    }
  }, []);

  const fetchActiveSessions = useCallback(async () => {
    // Don't fetch if user is null or not a student
    if (!user || user.role !== 'student') {
      setSessions([]);
      return;
    }

    // Don't fetch if polling is paused due to detected update
    if (pollingPausedRef.current) {
      logger.debug('Polling paused - update available');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Always add cache-busting parameter to ensure fresh data
      const endpoint = '/attendance/active-today?v=' + Date.now();
      const response = await apiService.request(endpoint);

      if (response.success) {
        setSessions(response.sessions || []);
        // Reset error count on successful fetch
        deploymentErrorCountRef.current = 0;
      } else {
        setError(response.message || 'Failed to fetch active sessions');
      }
    } catch (error) {
      // Handle permission errors gracefully - don't show error for non-students
      if (error.status === 403) {
        logger.debug('User does not have permission to access attendance sessions');
        setSessions([]);
        setError('');
        return;
      }

      // Check if this might be a deployment-related error
      if (isDeploymentRelatedError(error)) {
        deploymentErrorCountRef.current += 1;
        logger.warn('Possible deployment-related error in attendance fetch', {
          errorCount: deploymentErrorCountRef.current,
          error: error.message
        });

        // After 2 consecutive deployment-like errors, check for updates
        if (deploymentErrorCountRef.current >= 2) {
          const hasUpdate = await checkForUpdate();
          if (hasUpdate) {
            setError('');
            return; // Don't show error, update banner will be shown
          }
        }

        // Show a more helpful error message for deployment scenarios
        if (deploymentErrorCountRef.current >= 3) {
          setError('Connection interrupted. The app may be updating - please refresh the page.');
        } else {
          // Don't show error for first couple of failures - might be transient
          setError('');
        }
      } else {
        setError('Failed to fetch active sessions. Please try again.');
        deploymentErrorCountRef.current = 0;
      }
    } finally {
      setLoading(false);
    }
  }, [user, checkForUpdate]);


  useEffect(() => {
    if (user) {
      // Always fetch fresh data when component mounts
      fetchActiveSessions();
      
      // Refresh when user returns to the tab (focus event)
      const handleFocus = () => {
        logger.debug('Page focused - refreshing sessions');
        fetchActiveSessions();
      };
      
      // Refresh when page becomes visible (visibilitychange event)
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          logger.debug('Page visible - refreshing sessions');
          fetchActiveSessions();
        }
      };
      
      // Periodic refresh every 2 minutes to catch admin updates
      const interval = setInterval(() => {
        logger.debug('Periodic refresh - checking for session updates');
        fetchActiveSessions();
      }, 120000); // 2 minutes
      
      window.addEventListener('focus', handleFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      // Clear sessions when user is null
      setSessions([]);
    }
  }, [user, fetchActiveSessions]);



  // Backend now handles all filtering, so just return all sessions
  const getVisibleSessions = () => {
    return sessions || [];
    
    // Original filtering logic (commented out):
    // return sessions.filter(session => {
    //   const sessionStatus = getSessionStatus(session);
    //   
    //   // Always show future sessions (sessions for dates after today)
    //   if (session.date > today) {
    //     return true;
    //   }
    //   
    //   // Always show today's sessions regardless of status
    //   if (session.date === today) {
    //     return true;
    //   }
    //   
    //   // For past sessions, only show if they were checked in or recently ended
    //   if (sessionStatus === 'checked-in') {
    //     // Show for 1 hour after check-in window closes, then hide
    //     const checkInEndTime = new Date(`${session.date}T${session.checkin_end_time || session.end_time}`);
    //     const hideAfterTime = new Date(checkInEndTime.getTime() + 60 * 60 * 1000); // 1 hour later
    //     
    //     return now <= hideAfterTime;
    //   }
    //   
    //   // Show ended sessions for a short time (30 minutes) after session ends
    //   if (sessionStatus === 'ended') {
    //     const sessionEndTime = new Date(`${session.date}T${session.end_time}`);
    //     const hideAfterTime = new Date(sessionEndTime.getTime() + 30 * 60 * 1000); // 30 minutes later
    //     
    //     return now <= hideAfterTime;
    //   }
    //   
    //   // Hide old sessions that are outside check-in window and not checked in
    //   return false;
    // });
  };

  const handleCheckIn = async (sessionId) => {
    try {
      setCheckingIn(prev => new Set(prev).add(sessionId));
      setError('');
      setSuccess('');

      const response = await apiService.checkInAttendance(sessionId, 'present');

      if (response.success) {
        setSuccess('Successfully checked in for attendance!');

        // Refresh sessions to get the latest data from the server
        logger.debug('Check-in successful - refreshing sessions');
        await fetchActiveSessions();

        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Use the specific error message from the API response
        setError(response.message || response.error || 'Failed to check in for attendance');
      }
    } catch (error) {
      logger.error('Error checking in', { error, sessionId });
      // Try to extract meaningful error message from the error response
      let errorMessage = 'Failed to check in for attendance. Please try again.';

      // First, try to get message from response object
      if (error?.response?.message) {
        errorMessage = error.response.message;
      } else if (error?.response?.error) {
        errorMessage = error.response.error;
      } else if (error?.message) {
        // If the message looks like JSON, try to parse it
        if (typeof error.message === 'string' && error.message.includes('{')) {
          try {
            const parsed = JSON.parse(error.message);
            errorMessage = parsed.message || parsed.error || error.message;
          } catch (e) {
            // If parsing fails, use the original message
            errorMessage = error.message;
          }
        } else {
          errorMessage = error.message;
        }
      }

      setError(errorMessage);
    } finally {
      setCheckingIn(prev => {
        const newSet = new Set(prev);
        newSet.delete(sessionId);
        return newSet;
      });
    }
  };




  // Helper function to get session status
  const getSessionStatus = (session) => {
    // Checked-in state should always win, even after window closes
    if (hasCheckedInRecord(session)) {
      return 'checked-in';
    }

    // Show closed state only for students who did not check in
    if (
      session?.isPast ||
      session?.status === 'ended' ||
      session?.status === 'closed' ||
      session?.current_status === 'ended' ||
      session?.current_status === 'closed'
    ) {
      return 'ended';
    }

    return 'available';
  };

  const getCheckedInTimeLabel = (session) => {
    const source = getCheckedInTimestamp(session);
    if (!source) return null;

    const fromIso = DateTime.fromISO(source, { zone: 'utc' }).setZone('America/Chicago');
    if (fromIso.isValid) return fromIso.toFormat('h:mm a');

    const fromSql = DateTime.fromSQL(source, { zone: 'utc' }).setZone('America/Chicago');
    if (fromSql.isValid) return fromSql.toFormat('h:mm a');

    return null;
  };




  if (loading && sessions.length === 0) {
    return (
      <div className={styles.attendanceCard}>
        <div className={styles.attendanceHeader}>
          <h2 className={styles.attendanceTitle}>
            <FiClock size={20} />
            Active Attendance Sessions
          </h2>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <span>Loading active sessions...</span>
        </div>
      </div>
    );
  }

  // Don't show the component at all if there are no sessions or no visible sessions
  if (sessions.length === 0 || getVisibleSessions().length === 0) {
    return null;
  }

  return (
    <div className={styles.attendanceCard}>
      <div className={styles.attendanceHeader}>
        <h2 className={styles.attendanceTitle}>
          <FiClock size={20} />
          Active Attendance Sessions
        </h2>
      </div>

      <div className={styles.attendanceList}>
        {/* Update available banner */}
        {updateAvailable && (
          <motion.div
            className={styles.updateBanner}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className={styles.updateBannerContent}>
              <FiRefreshCw size={16} />
              <span>A new version is available. Please refresh to continue.</span>
            </div>
            <button
              className={styles.updateRefreshButton}
              onClick={() => window.location.reload(true)}
            >
              Refresh Now
            </button>
          </motion.div>
        )}

        {success && (
          <motion.div
            className={styles.successMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiCheck size={16} />
            {success}
          </motion.div>
        )}

        {error && !updateAvailable && (
          <motion.div
            className={styles.errorMessage}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FiAlertCircle size={16} />
            {error}
          </motion.div>
        )}

        {getVisibleSessions().map((session, index) => (
            <motion.div
              key={session.session_id}
              className={styles.attendanceItem}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
            <div className={styles.itemHeader}>
              <div className={styles.itemInfo}>
                <h3 className={styles.itemTitle}>
                  <FiBookOpen size={18} />
                  {session.title || `${session.course_name || session.elective_name} Session`}
                </h3>
                <div className={styles.metaRow}>
                  <span className={styles.metaItem}>
                    <FiCalendar size={14} />
                    {utcToChicago(session.start_time).toFormat('EEEE, MMMM d, yyyy')}
                  </span>
                  <span className={styles.metaSeparator}>•</span>
                  <span className={styles.metaItem}>
                    <FiUser size={14} />
                    {session.entity_type === 'elective' && session.instructors
                      ? session.instructors
                      : `${session.instructor_first_name} ${session.instructor_last_name}`}
                  </span>
                  <span className={styles.metaSeparator}>•</span>
                  <span className={styles.metaItem}>
                    <FiBookOpen size={14} />
                    {session.course_code || session.elective_code} - {session.course_name || session.elective_name}
                    {session.entity_type === 'elective' && ' (Elective)'}
                  </span>
                </div>
              </div>
              {(() => {
                const sessionStatus = getSessionStatus(session);
                
                // TEMPORARILY DISABLED: No more future sessions, all are available for check-in
                // if (sessionStatus === 'future') {
                //   return (
                //     <CheckInButton
                //       style={{ 
                //         backgroundColor: '#3b82f6', 
                //         color: 'white',
                //         cursor: 'default'
                //       }}
                //       disabled={true}
                //     >
                //       <FiCalendar size={16} />
                //       Upcoming
                //     </CheckInButton>
                //   );
                // }
                
                if (sessionStatus === 'checked-in') {
                  const checkedInTimeLabel = getCheckedInTimeLabel(session);
                  return (
                    <button
                      className={`${styles.checkInButton} ${styles.checkedIn}`}
                      disabled={true}
                    >
                      <FiCheck size={16} />
                      {checkedInTimeLabel ? `Checked In • ${checkedInTimeLabel}` : 'Checked In'}
                    </button>
                  );
                }
                
                if (sessionStatus === 'ended') {
                  return (
                    <button
                      className={`${styles.checkInButton} ${styles.sessionEnded}`}
                      disabled={true}
                    >
                      <FiClock size={16} />
                      Check In Closed
                    </button>
                  );
                }
                
                // TEMPORARILY DISABLED: No more outside-window logic, all sessions are available
                // if (sessionStatus === 'outside-window') {
                //   // Check if we're before or after the check-in window
                //   const chicagoNow = DateTime.now().setZone('America/Chicago');
                //   const currentTime = chicagoNow.toFormat('HH:mm:ss');
                //   
                //   // If we have check-in times, determine if we're before or after
                //   if (session.checkin_start_time && session.checkin_end_time) {
                //     if (currentTime < session.checkin_start_time) {
                //       // We're before the check-in window
                //       // Session is upcoming (before check-in window)
                //       
                //       return (
                //         <CheckInButton
                //           style={{ 
                //             backgroundColor: '#3b82f6', 
                //             color: 'white',
                //             cursor: 'default'
                //           }}
                //           disabled={true}
                //         >
                //           <FiClock size={16} />
                //           Upcoming
                //         </CheckInButton>
                //       );
                //     } else {
                //       // We're after the check-in window
                //       return (
                //         <CheckInButton
                //           style={{ 
                //             backgroundColor: '#f59e0b', 
                //             color: 'white',
                //             cursor: 'default'
                //           }}
                //           disabled={true}
                //         >
                //           <FiClock size={16} />
                //           Check-in Closed
                //         </CheckInButton>
                //       );
                //     }
                //   }
                //   
                //   // Fallback to "Check-in Closed" if no check-in times
                //   return (
                //     <CheckInButton
                //       style={{ 
                //         backgroundColor: '#f59e0b', 
                //         color: 'white',
                //         cursor: 'default'
                //       }}
                //       disabled={true}
                //     >
                //       <FiClock size={16} />
                //       Check-in Closed
                //     </CheckInButton>
                //   );
                // }
                
                // Available for check-in
                return (
                  <button
                    className={styles.checkInButton}
                    onClick={() => handleCheckIn(session.session_id)}
                    disabled={checkingIn.has(session.session_id)}
                  >
                    {checkingIn.has(session.session_id) ? (
                      <>
                        <div className={styles.loadingSpinner} />
                        Checking In...
                      </>
                    ) : (
                      <>
                        <FiCheck size={16} />
                        Check In
                      </>
                    )}
                  </button>
                );
              })()}
            </div>
            
            <div className={styles.timeWindow}>
              <FiClock size={16} />
              {(() => {
                const sessionStatus = getSessionStatus(session);
                
                // TEMPORARILY DISABLED: No more future sessions, all are available
                // if (sessionStatus === 'future') {
                //   return `Check-in window: ${getSessionTimeWindowText(session)}`;
                // }
                
                if (sessionStatus === 'checked-in') {
                  const checkedInTimeLabel = getCheckedInTimeLabel(session);
                  if (checkedInTimeLabel) {
                    return `Checked in at ${checkedInTimeLabel}`;
                  }
                  return 'Checked in';
                }
                
                if (sessionStatus === 'available') {
                  // Show actual time window if available
                  if (session.start_time && session.end_time) {
                    // Format start time with date: "Mon, Nov 17, 8:00 PM"
                    const startDateTime = DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
                    const startTime = startDateTime.toFormat('EEE, MMM d, h:mm a');
                    
                    // Format end time - only include date if different day
                    const endDateTime = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
                    const isSameDay = startDateTime.hasSame(endDateTime, 'day');
                    const endTime = isSameDay 
                      ? endDateTime.toFormat('h:mm a')  // Just time if same day: "10:00 PM"
                      : endDateTime.toFormat('EEE, MMM d, h:mm a');  // Full date if different day
                    
                    return `Check-in window: ${startTime} - ${endTime}`;
                  }
                  return 'Check-in available anytime today';
                }
                
                // Fallback: show actual time window if available
                if (session.start_time && session.end_time) {
                  // Format start time with date: "Mon, Nov 17, 8:00 PM"
                  const startDateTime = DateTime.fromISO(session.start_time, { zone: 'utc' }).setZone('America/Chicago');
                  const startTime = startDateTime.toFormat('EEE, MMM d, h:mm a');
                  
                  // Format end time - only include date if different day
                  const endDateTime = DateTime.fromISO(session.end_time, { zone: 'utc' }).setZone('America/Chicago');
                  const isSameDay = startDateTime.hasSame(endDateTime, 'day');
                  const endTime = isSameDay 
                    ? endDateTime.toFormat('h:mm a')  // Just time if same day: "10:00 PM"
                    : endDateTime.toFormat('EEE, MMM d, h:mm a');  // Full date if different day
                  
                  return `Check-in window: ${startTime} - ${endTime}`;
                }
                return 'Check-in available anytime today';
              })()}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

export default ActiveAttendanceSessions;
